import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	Account,
	AssignSubAccountPropertyBody,
	CompanyDocument,
	CompanyProfile,
	ConfirmEmailChangeBody,
	ConfirmPhoneChangeBody,
	InitEmailChangeBody,
	InitPhoneChangeBody,
	InviteSubAccountBody,
	ListCompanyDocumentsResponse,
	ListSubAccountsParams,
	Organization,
	CreateRoleBody,
	RoleDefinition,
	RoleVocabularyEntry,
	SaveRoleBody,
	SubAccount,
	UpdateAccountTypeBody,
	UpdateCurrencyBody,
	UpdateOrganizationSegmentBody,
	UpdateTaxRegimeBody,
	UpdateSubAccountBody,
	UpdateTelegramBody,
	UploadCompanyDocumentBody,
	UpsertCompanyProfileBody,
} from './types/account'

export const accountApi = createApi({
	reducerPath: 'accountApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Account', 'CompanyProfile', 'CompanyDocument', 'SubAccount', 'Organization', 'Role'],
	endpoints: (builder) => ({
		getAccount: builder.query<Account, string>({
			query: (id) => `/account/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Account', id }],
		}),

		updateAccountType: builder.mutation<Account, UpdateAccountTypeBody>({
			query: (body) => ({ url: '/account/type', method: 'PATCH', body }),
			invalidatesTags: ['Account'],
		}),

		updateTaxRegime: builder.mutation<Account, UpdateTaxRegimeBody>({
			query: (body) => ({ url: '/account/tax-regime', method: 'PATCH', body }),
			invalidatesTags: ['Account'],
		}),

		updateCurrency: builder.mutation<Account, UpdateCurrencyBody>({
			query: (body) => ({ url: '/account/currency', method: 'PATCH', body }),
			invalidatesTags: ['Account'],
		}),

		initEmailChange: builder.mutation<void, InitEmailChangeBody>({
			query: (body) => ({ url: '/account/email/init', method: 'POST', body }),
		}),

		confirmEmailChange: builder.mutation<void, ConfirmEmailChangeBody>({
			query: (body) => ({ url: '/account/email/confirm', method: 'POST', body }),
			invalidatesTags: ['Account'],
		}),

		initPhoneChange: builder.mutation<void, InitPhoneChangeBody>({
			query: (body) => ({ url: '/account/phone/init', method: 'POST', body }),
		}),

		confirmPhoneChange: builder.mutation<void, ConfirmPhoneChangeBody>({
			query: (body) => ({ url: '/account/phone/confirm', method: 'POST', body }),
			invalidatesTags: ['Account'],
		}),

		updateTelegram: builder.mutation<void, UpdateTelegramBody>({
			query: (body) => ({ url: '/account/telegram', method: 'PUT', body }),
			invalidatesTags: ['Account'],
		}),

		getCompanyProfile: builder.query<CompanyProfile | null, void>({
			query: () => '/account/company',
			providesTags: ['CompanyProfile'],
		}),

		upsertCompanyProfile: builder.mutation<CompanyProfile | null, UpsertCompanyProfileBody>({
			query: (body) => ({ url: '/account/company', method: 'PUT', body }),
			invalidatesTags: ['CompanyProfile'],
		}),

		// ── Organization: its segment decides which team roles it offers ──

		getOrganization: builder.query<Organization, void>({
			query: () => '/account/organization',
			providesTags: ['Organization'],
		}),

		// The response is the updated organization: write it into the cache so
		// the segment and the role lists change at once, without a refetch.
		updateOrganizationSegment: builder.mutation<Organization, UpdateOrganizationSegmentBody>({
			query: (body) => ({ url: '/account/organization/segment', method: 'PATCH', body }),
			async onQueryStarted(_body, { dispatch, queryFulfilled }) {
				const { data } = await queryFulfilled.catch(() => ({ data: undefined }))
				if (data) dispatch(accountApi.util.upsertQueryData('getOrganization', undefined, data))
			},
		}),

		// ── Roles: the platform's super admin (ADR 0003) ──
		// Team changes refetch the roles too: their member counts decide
		// whether a role can be removed.

		listRoles: builder.query<RoleDefinition[], void>({
			query: () => '/admin/roles',
			providesTags: ['Role'],
		}),

		getRoleVocabulary: builder.query<RoleVocabularyEntry[], void>({
			query: () => '/admin/roles/vocabulary',
		}),

		// A changed role changes what organizations may give, so their lists
		// are refetched too.
		createRole: builder.mutation<RoleDefinition, CreateRoleBody>({
			query: (body) => ({ url: '/admin/roles', method: 'POST', body }),
			invalidatesTags: ['Role', 'Organization'],
		}),

		updateRole: builder.mutation<RoleDefinition, { key: string; body: SaveRoleBody }>({
			query: ({ key, body }) => ({ url: `/admin/roles/${key}`, method: 'PUT', body }),
			invalidatesTags: ['Role', 'Organization', 'SubAccount'],
		}),

		deleteRole: builder.mutation<OkResponse, string>({
			query: (key) => ({ url: `/admin/roles/${key}`, method: 'DELETE' }),
			invalidatesTags: ['Role', 'Organization'],
		}),

		listSubAccounts: builder.query<SubAccount[], ListSubAccountsParams | void>({
			query: (params) => ({ url: '/account/sub-accounts', params: params ?? {} }),
			providesTags: ['SubAccount'],
		}),

		inviteSubAccount: builder.mutation<SubAccount, InviteSubAccountBody>({
			query: (body) => ({ url: '/account/sub-accounts', method: 'POST', body }),
			invalidatesTags: ['SubAccount', 'Role'],
		}),

		updateSubAccount: builder.mutation<SubAccount, { id: string; body: UpdateSubAccountBody }>({
			query: ({ id, body }) => ({ url: `/account/sub-accounts/${id}`, method: 'PATCH', body }),
			invalidatesTags: ['SubAccount', 'Role'],
		}),

		deleteSubAccount: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/account/sub-accounts/${id}`, method: 'DELETE' }),
			invalidatesTags: ['SubAccount', 'Role'],
		}),

		assignSubAccountProperty: builder.mutation<OkResponse, { id: string; body: AssignSubAccountPropertyBody }>({
			query: ({ id, body }) => ({ url: `/account/sub-accounts/${id}/properties`, method: 'POST', body }),
			invalidatesTags: ['SubAccount'],
		}),

		unassignSubAccountProperty: builder.mutation<OkResponse, { id: string; propertyId: string }>({
			query: ({ id, propertyId }) => ({
				url: `/account/sub-accounts/${id}/properties/${propertyId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['SubAccount'],
		}),

		// ── Company documents (Свидетельство, Выписка ЕГРЮЛ и т.п.) ──

		listCompanyDocuments: builder.query<ListCompanyDocumentsResponse, void>({
			query: () => '/account/documents',
			providesTags: ['CompanyDocument'],
		}),

		uploadCompanyDocument: builder.mutation<CompanyDocument, UploadCompanyDocumentBody>({
			query: (body) => ({ url: '/account/documents', method: 'POST', body }),
			invalidatesTags: ['CompanyDocument'],
		}),

		deleteCompanyDocument: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/account/documents/${id}`, method: 'DELETE' }),
			invalidatesTags: ['CompanyDocument'],
		}),
	}),
})

export const {
	useGetAccountQuery,
	useListRolesQuery,
	useGetRoleVocabularyQuery,
	useCreateRoleMutation,
	useUpdateRoleMutation,
	useDeleteRoleMutation,
	useGetOrganizationQuery,
	useUpdateOrganizationSegmentMutation,
	useUpdateAccountTypeMutation,
	useUpdateCurrencyMutation,
	useUpdateTaxRegimeMutation,
	useInitEmailChangeMutation,
	useConfirmEmailChangeMutation,
	useInitPhoneChangeMutation,
	useConfirmPhoneChangeMutation,
	useUpdateTelegramMutation,
	useGetCompanyProfileQuery,
	useUpsertCompanyProfileMutation,
	useListSubAccountsQuery,
	useInviteSubAccountMutation,
	useUpdateSubAccountMutation,
	useDeleteSubAccountMutation,
	useAssignSubAccountPropertyMutation,
	useUnassignSubAccountPropertyMutation,
	useListCompanyDocumentsQuery,
	useUploadCompanyDocumentMutation,
	useDeleteCompanyDocumentMutation,
} = accountApi
